/**
 * 🎨 V2X Three.js UI控制器 - 你的3D界面管理专家
 *
 * 这个文件就像是"电影院的控制面板"🎬，负责创建和管理3D场景中的
 * 所有用户界面元素，比如全屏按钮、性能监控、加载画面等！
 *
 * 🤔 什么是UI控制器？
 * 想象你在看电影：
 * 1. 🔲 全屏按钮：让电影占满整个屏幕
 * 2. 📊 性能监控：显示画面的流畅度信息
 * 3. ⏳ 加载动画：告诉你电影正在缓冲
 * 4. 🎨 主题切换：亮色/暗色界面
 *
 * 🎭 界面元素类型：
 * - 🔲 全屏控制：一键进入/退出全屏模式
 * - 📊 性能显示：实时FPS、分辨率等信息
 * - ⏳ 加载指示器：美观的云朵加载动画
 * - 🎨 主题系统：支持明亮/黑暗/自动主题
 * - 📍 位置配置：四个角落任意放置
 */

/**
 * 🎛️ UI控制器配置接口 - 你的个性化设置清单
 *
 * 这个接口就像"遥控器的按钮配置"🎮，让你决定哪些功能要显示，
 * 界面要什么样子，放在屏幕的哪个位置！
 */
export interface UIControlsConfig {
  showFullscreenButton?: boolean; // 🔲 是否显示全屏按钮（默认true）
  showPerformanceInfo?: boolean; // 📊 是否显示性能信息（默认false）
  showLoadingIndicator?: boolean; // ⏳ 是否显示加载指示器（默认true）
  theme?: 'light' | 'dark' | 'auto'; // 🎨 界面主题（light明亮/dark黑暗/auto自动）
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'; // 📍 控件位置
}

/*
 * 📱 关于主题的小知识：
 * - light：明亮主题，适合白天使用，背景较亮
 * - dark：黑暗主题，适合夜间使用，背景较暗
 * - auto：自动主题，根据系统设置或时间自动切换
 *
 * 就像手机的深色模式，不同场景用不同的颜色保护眼睛！
 */

/**
 * 🎨 UI控制器主类 - 你的界面管理大师
 *
 * 这个类就像是"舞台监督"🎭，负责在3D场景上方创建和管理
 * 所有的用户界面元素，让用户能够方便地控制和监控3D世界！
 */
export class UIControls {
  // 🎯 核心属性
  private container: HTMLElement; // 🖼️ 父容器元素（通常是canvas的父元素）
  private config: UIControlsConfig; // ⚙️ 配置选项
  private fullscreenButton?: HTMLElement; // 🔲 全屏按钮元素
  private performanceInfo?: HTMLElement; // 📊 性能信息显示元素
  private loadingIndicator?: HTMLElement; // ⏳ 加载指示器元素
  private isFullscreen: boolean = false; // 🔍 当前是否处于全屏状态

  /**
   * 🏗️ 构造函数 - 创建UI控制器
   *
   * 就像搭建一个"控制台"🎛️，你需要：
   * 1. 🖼️ 指定在哪个容器里创建界面
   * 2. ⚙️ 设置需要什么功能和样式
   * 3. 🎨 自动初始化所有界面元素
   *
   * @param container 父容器元素，UI控件会添加到这里
   * @param config 可选的配置选项
   */
  constructor(container: HTMLElement, config: UIControlsConfig = {}) {
    this.container = container;

    // 🛠️ 合并默认配置和用户配置
    this.config = {
      showFullscreenButton: true, // 🔲 默认显示全屏按钮
      showPerformanceInfo: false, // 📊 默认隐藏性能信息（避免界面太杂乱）
      showLoadingIndicator: true, // ⏳ 默认显示加载指示器
      theme: 'dark', // 🎨 默认深色主题（更适合3D场景）
      position: 'top-right', // 📍 默认放在右上角
      ...config, // 🔀 用户配置覆盖默认配置
    };

    this.init(); // 🚀 开始初始化界面
  }

  /**
   * 🚀 初始化UI控件 - 启动界面搭建
   *
   * 这个方法就像"开工仪式"🎉，按照配置依次创建需要的界面元素：
   * 1. 🎨 创建CSS样式（界面的"化妆"）
   * 2. 🔲 创建全屏按钮（如果需要）
   * 3. 📊 创建性能监控（如果需要）
   * 4. ⏳ 创建加载动画（如果需要）
   * 5. 👂 监听全屏状态变化
   */
  private init() {
    this.createStyles(); // 🎨 先创建样式表

    if (this.config.showFullscreenButton) {
      this.createFullscreenButton(); // 🔲 创建全屏按钮
    }

    if (this.config.showPerformanceInfo) {
      this.createPerformanceInfo(); // 📊 创建性能信息显示
    }

    if (this.config.showLoadingIndicator) {
      this.createLoadingIndicator(); // ⏳ 创建加载指示器
    }

    // 👂 监听全屏状态变化（当用户按F11或其他方式进入全屏时）
    document.addEventListener(
      'fullscreenchange',
      this.onFullscreenChange.bind(this)
    );
  }

  /**
   * 🎨 创建样式表 - 给界面"化妆"
   *
   * 这个方法就像"设计师的调色盘"🎭，定义了所有UI元素的外观：
   * - 🔲 按钮的圆角、阴影、悬停效果
   * - 📊 性能信息的字体、背景、布局
   * - ⏳ 加载动画的云朵飘动效果
   * - 🎨 亮色/暗色主题的颜色方案
   */
  private createStyles() {
    const styleId = 'v2x-three-ui-styles';
    if (document.getElementById(styleId)) return; // 🔍 防止重复创建样式

    const style = document.createElement('style');
    style.id = styleId;

    // 🎨 定义完整的CSS样式（包含现代设计和动画效果）
    style.textContent = `
      /* 🎛️ 主控件容器 - 界面元素的"舞台" */
      .v2x-ui-controls {
        position: absolute;        /* 🎯 绝对定位，浮在3D场景上方 */
        z-index: 1000;           /* 📈 高层级，确保在3D内容之上 */
        pointer-events: none;     /* 🖱️ 容器本身不拦截鼠标事件 */
      }
      
      /* 📍 四个角落的位置定义 */
      .v2x-ui-controls.top-right {
        top: 20px;    /* ⬆️ 距离顶部20像素 */
        right: 20px;  /* ➡️ 距离右边20像素 */
      }
      
      .v2x-ui-controls.top-left {
        top: 20px;    /* ⬆️ 距离顶部20像素 */
        left: 20px;   /* ⬅️ 距离左边20像素 */
      }
      
      .v2x-ui-controls.bottom-right {
        bottom: 20px; /* ⬇️ 距离底部20像素 */
        right: 20px;  /* ➡️ 距离右边20像素 */
      }
      
      .v2x-ui-controls.bottom-left {
        bottom: 20px; /* ⬇️ 距离底部20像素 */
        left: 20px;   /* ⬅️ 距离左边20像素 */
      }
      
      /* 🔲 通用按钮样式 - 现代玻璃质感设计 */
      .v2x-ui-button {
        display: inline-flex;          /* 🎯 弹性布局，内容居中 */
        align-items: center;           /* 📐 垂直居中 */
        justify-content: center;       /* 📐 水平居中 */
        width: 48px;                   /* 📏 按钮宽度 */
        height: 48px;                  /* 📏 按钮高度 */
        margin: 4px;                   /* 📏 按钮间距 */
        border: none;                  /* 🚫 去掉默认边框 */
        border-radius: 12px;           /* 🔄 圆角设计 */
        background: rgba(0, 0, 0, 0.7); /* 🎨 半透明黑色背景 */
        color: white;                  /* 🎨 白色图标/文字 */
        cursor: pointer;               /* 🖱️ 鼠标悬停显示手型 */
        pointer-events: auto;          /* 🖱️ 按钮可以接收鼠标事件 */
        transition: all 0.2s ease;     /* ✨ 平滑过渡动画 */
        backdrop-filter: blur(10px);   /* 🌟 毛玻璃效果（模糊背景） */
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); /* 💫 柔和阴影 */
      }
      
      /* 🖱️ 按钮悬停效果 - 鼠标悬停时的反馈 */
      .v2x-ui-button:hover {
        background: rgba(0, 0, 0, 0.8); /* 🎨 悬停时背景更深 */
        transform: translateY(-2px);    /* 📈 向上浮起2像素 */
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2); /* 💫 更深的阴影 */
      }
      
      /* 🖱️ 按钮点击效果 - 提供点击反馈 */
      .v2x-ui-button:active {
        transform: translateY(0);       /* 📉 点击时回到原位 */
      }
      
      /* ☀️ 亮色主题按钮样式 */
      .v2x-ui-button.light {
        background: rgba(255, 255, 255, 0.9); /* 🎨 半透明白色背景 */
        color: #333;                           /* 🎨 深色图标/文字 */
      }
      
      .v2x-ui-button.light:hover {
        background: rgba(255, 255, 255, 0.95); /* 🎨 悬停时更不透明 */
      }
      
      /* 📊 性能信息显示样式 - 程序员风格的监控面板 */
      .v2x-performance-info {
        display: flex;                 /* 🎯 弹性布局 */
        flex-direction: column;        /* 📐 垂直排列 */
        gap: 4px;                      /* 📏 行间距 */
        padding: 12px 16px;            /* 📏 内边距 */
        margin: 4px;                   /* 📏 外边距 */
        border-radius: 12px;           /* 🔄 圆角设计 */
        background: rgba(0, 0, 0, 0.7); /* 🎨 半透明黑色背景 */
        color: white;                  /* 🎨 白色文字 */
        font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace; /* 🔤 等宽字体 */
        font-size: 12px;               /* 🔤 小字号 */
        pointer-events: auto;          /* 🖱️ 可以接收鼠标事件 */
        backdrop-filter: blur(10px);   /* 🌟 毛玻璃效果 */
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); /* 💫 柔和阴影 */
      }
      
      /*
       * 🔤 关于等宽字体的小知识：
       * 等宽字体中每个字符占用相同的宽度，这样数字对齐更整齐。
       * 'SF Mono'是苹果系统字体，'Monaco'是经典等宽字体，
       * 'Cascadia Code'是微软的现代编程字体，还有连字符功能！
       * 
       * 就像表格一样，数字需要对齐才好看：
       * FPS: 60  ✅ 整齐
       * FPS: 120 ✅ 整齐
       * 而不是：
       * FPS: 60  ❌ 不整齐
       * FPS: 120 ❌ 不整齐
       */
      
      /* ⏳ 全屏加载指示器 - 美观的加载动画 */
      .v2x-loading-indicator {
        position: fixed;               /* 📌 固定定位，覆盖整个屏幕 */
        top: 0;                        /* ⬆️ 顶部对齐 */
        left: 0;                       /* ⬅️ 左边对齐 */
        width: 100%;                   /* 📏 全屏宽度 */
        height: 100%;                  /* 📏 全屏高度 */
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); /* 🎨 渐变背景 */
        display: flex;                 /* 🎯 弹性布局 */
        flex-direction: column;        /* 📐 垂直排列 */
        align-items: center;           /* 📐 水平居中 */
        justify-content: center;       /* 📐 垂直居中 */
        z-index: 9999;                 /* 📈 最高层级，确保在所有内容之上 */
        pointer-events: auto;          /* 🖱️ 拦截所有鼠标事件（防止点击穿透） */
      }
      
      /* ☁️ 云朵动画容器 - 创造飘动的云朵效果 */
      .v2x-loading-cloud {
        width: 100px;                  /* 📏 云朵容器宽度 */
        height: 60px;                  /* 📏 云朵容器高度 */
        position: relative;            /* 📌 相对定位，为子元素提供定位基准 */
        margin-bottom: 40px;           /* 📏 与下方文字的间距 */
      }
      
      /* ☁️ 云朵的两个组成部分 - 用伪元素创建云朵形状 */
      .v2x-loading-cloud::before,
      .v2x-loading-cloud::after {
        content: '';                   /* 📝 伪元素必须有content属性 */
        position: absolute;            /* 📌 绝对定位 */
        background: rgba(255, 255, 255, 0.8); /* 🎨 半透明白色 */
        border-radius: 50px;           /* 🔄 圆角，创造云朵的圆润效果 */
      }
      
      /* ☁️ 云朵的左半部分 */
      .v2x-loading-cloud::before {
        width: 50px;                   /* 📏 左云朵宽度 */
        height: 50px;                  /* 📏 左云朵高度 */
        top: 10px;                     /* 📍 距离容器顶部位置 */
        left: 10px;                    /* 📍 距离容器左边位置 */
        animation: cloudFloat1 3s ease-in-out infinite; /* ✨ 飘动动画1 */
      }
      
      /* ☁️ 云朵的右半部分 */
      .v2x-loading-cloud::after {
        width: 60px;                   /* 📏 右云朵宽度 */
        height: 40px;                  /* 📏 右云朵高度 */
        top: 20px;                     /* 📍 距离容器顶部位置 */
        right: 15px;                   /* 📍 距离容器右边位置 */
        animation: cloudFloat2 3s ease-in-out infinite 0.5s; /* ✨ 飘动动画2，延迟0.5秒 */
      }
      
      /* 📝 加载文字样式 */
      .v2x-loading-text {
        color: white;                  /* 🎨 白色文字 */
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; /* 🔤 系统字体 */
        font-size: 18px;               /* 🔤 字体大小 */
        font-weight: 300;              /* 🔤 细体重量 */
        margin-top: 20px;              /* 📏 与上方云朵的间距 */
        animation: textPulse 2s ease-in-out infinite; /* ✨ 文字脉冲动画 */
      }
      
      /* ✨ 云朵飘动动画1 - 左边云朵的运动轨迹 */
      @keyframes cloudFloat1 {
        0%, 100% { 
          transform: translateY(0px);  /* 📍 开始和结束位置 */
          opacity: 0.8;                /* 🎨 透明度 */
        }
        50% { 
          transform: translateY(-10px); /* 📈 中间位置向上10像素 */
          opacity: 1;                   /* 🎨 完全不透明 */
        }
      }
      
      /* ✨ 云朵飘动动画2 - 右边云朵的运动轨迹 */
      @keyframes cloudFloat2 {
        0%, 100% { 
          transform: translateY(0px) scale(1); /* 📍 开始和结束位置，正常缩放 */
          opacity: 0.7;                        /* 🎨 透明度 */
        }
        50% { 
          transform: translateY(-15px) scale(1.1); /* 📈 中间位置向上15像素，放大1.1倍 */
          opacity: 0.9;                            /* 🎨 更不透明 */
        }
      }
      
      /* ✨ 文字脉冲动画 - 文字的呼吸效果 */
      @keyframes textPulse {
        0%, 100% { opacity: 0.7; }      /* 🎨 开始和结束较透明 */
        50% { opacity: 1; }              /* 🎨 中间完全不透明 */
      }
    `;

    document.head.appendChild(style); // 📄 将样式添加到页面头部
  }

  /*
   * 🎨 关于CSS动画的小知识：
   *
   * 1. @keyframes：定义动画的关键帧
   *    - 0%：动画开始时的状态
   *    - 50%：动画进行到一半时的状态
   *    - 100%：动画结束时的状态
   *
   * 2. animation属性：应用动画到元素
   *    - cloudFloat1：动画名称
   *    - 3s：动画持续时间
   *    - ease-in-out：缓动函数（慢-快-慢）
   *    - infinite：无限循环
   *    - 0.5s：延迟时间
   *
   * 3. transform：变换操作
   *    - translateY()：垂直移动
   *    - scale()：缩放
   *
   * 就像制作动画片，每个关键帧是一张画，浏览器自动补间产生流畅动画！
   */

  /**
   * 🔲 创建全屏按钮 - 你的全屏控制专家
   *
   * 这个方法就像"安装遥控器"📺，创建一个漂亮的全屏按钮，
   * 让用户可以一键进入沉浸式的全屏3D体验！
   */
  private createFullscreenButton() {
    const controlsContainer = this.getControlsContainer(); // 🎯 获取控件容器

    // 🔲 创建按钮元素
    this.fullscreenButton = document.createElement('button');
    this.fullscreenButton.className = `v2x-ui-button ${this.config.theme}`; // 🎨 应用主题样式
    this.fullscreenButton.innerHTML = this.getFullscreenIcon(false); // 🖼️ 设置非全屏状态图标
    this.fullscreenButton.title = '全屏'; // 💭 鼠标悬停提示

    // 👂 监听点击事件
    this.fullscreenButton.addEventListener(
      'click',
      this.toggleFullscreen.bind(this) // 🔄 绑定切换全屏方法
    );

    controlsContainer.appendChild(this.fullscreenButton); // ➕ 添加到控件容器
  }

  /**
   * 📊 创建性能信息显示 - 你的3D性能监控仪表盘
   *
   * 这个方法就像"安装汽车仪表盘"🚗，显示3D场景的实时性能数据：
   * - 🎯 FPS（每秒帧数）：画面流畅度指标
   * - 📏 分辨率：画面清晰度信息
   * - 🔍 像素比：屏幕像素密度
   */
  private createPerformanceInfo() {
    const controlsContainer = this.getControlsContainer(); // 🎯 获取控件容器

    // 📊 创建性能信息容器
    this.performanceInfo = document.createElement('div');
    this.performanceInfo.className = 'v2x-performance-info';

    // 📋 创建性能信息内容（使用ID方便后续更新）
    this.performanceInfo.innerHTML = `
      <div>FPS: <span id="v2x-fps">--</span></div>
      <div>分辨率: <span id="v2x-resolution">--</span></div>
      <div>像素比: <span id="v2x-pixel-ratio">--</span></div>
    `;

    controlsContainer.appendChild(this.performanceInfo); // ➕ 添加到控件容器
  }

  /*
   * 📊 关于性能指标的小知识：
   *
   * 1. FPS（Frames Per Second）：每秒帧数
   *    - 60 FPS：非常流畅，游戏级别
   *    - 30 FPS：基本流畅，可接受
   *    - <20 FPS：卡顿，需要优化
   *
   * 2. 分辨率：画面的像素尺寸
   *    - 1920x1080：Full HD（全高清）
   *    - 2560x1440：2K（超高清）
   *    - 3840x2160：4K（极高清）
   *
   * 3. 像素比：物理像素与CSS像素的比例
   *    - 1.0：普通屏幕
   *    - 2.0：高清屏（如Retina）
   *    - 3.0：超高清屏
   *
   * 就像体检报告，这些数字告诉你3D场景的"健康状况"！
   */

  /**
   * ⏳ 创建加载指示器 - 你的等待时光美化师
   *
   * 这个方法就像"设计等候室"🏥，创建一个美观的全屏加载动画，
   * 让用户在等待3D模型加载时也能享受视觉盛宴！
   */
  private createLoadingIndicator() {
    // ⏳ 创建加载指示器元素
    this.loadingIndicator = document.createElement('div');
    this.loadingIndicator.className = 'v2x-loading-indicator';

    // 🎭 创建加载动画内容（云朵 + 文字）
    this.loadingIndicator.innerHTML = `
      <div class="v2x-loading-cloud"></div>
      <div class="v2x-loading-text">正在加载模型...</div>
    `;

    document.body.appendChild(this.loadingIndicator); // ➕ 添加到页面body（全屏覆盖）
  }

  /**
   * 🎯 获取控件容器 - 界面元素的"舞台"
   *
   * 这个方法就像"准备舞台"🎭，确保有一个专门的容器来放置
   * 所有的UI控件，如果没有就创建一个！
   *
   * @returns HTMLElement 控件容器元素
   */
  private getControlsContainer(): HTMLElement {
    // 🔍 先尝试找到现有的控件容器
    let controlsContainer = this.container.querySelector(
      '.v2x-ui-controls'
    ) as HTMLElement;

    if (!controlsContainer) {
      // 🏗️ 如果没有找到，就创建一个新的容器
      controlsContainer = document.createElement('div');
      controlsContainer.className = `v2x-ui-controls ${this.config.position}`; // 🎨 应用位置样式
      this.container.appendChild(controlsContainer); // ➕ 添加到父容器
    }

    return controlsContainer;
  }

  /**
   * 🖼️ 获取全屏图标 - 根据状态显示不同图标
   *
   * 这个方法就像"换电视图标"📺，根据当前是否全屏显示不同的SVG图标：
   * - 📤 非全屏时：显示"进入全屏"图标（向外扩展箭头）
   * - 📥 全屏时：显示"退出全屏"图标（向内收缩箭头）
   *
   * @param isFullscreen 当前是否处于全屏状态
   * @returns SVG图标的HTML字符串
   */
  private getFullscreenIcon(isFullscreen: boolean): string {
    return isFullscreen
      ? `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
        </svg>` // 📥 退出全屏图标（箭头向内）
      : `<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
        </svg>`; // 📤 进入全屏图标（箭头向外）
  }

  /*
   * 🖼️ 关于SVG图标的小知识：
   *
   * SVG（Scalable Vector Graphics）是矢量图形格式：
   * 1. 📐 可以无损缩放到任意大小
   * 2. 🎨 可以用CSS控制颜色（fill="currentColor"）
   * 3. 📦 文件小，加载快
   * 4. 🔍 支持搜索引擎索引
   *
   * viewBox="0 0 24 24"定义了24x24的坐标系统，
   * path元素用SVG路径语言描述图形形状。
   *
   * 就像用数学公式描述图形，永远不会模糊！
   */

  /**
   * 🔄 切换全屏 - 全屏模式的开关大师
   *
   * 这个方法就像"电影院的放映控制"🎬，可以让3D场景在
   * 窗口模式和全屏模式之间自由切换！
   */
  private async toggleFullscreen() {
    try {
      if (!this.isFullscreen) {
        // 📤 进入全屏模式
        await this.container.requestFullscreen(); // 🚀 请求全屏
      } else {
        // 📥 退出全屏模式
        await document.exitFullscreen(); // 🔙 退出全屏
      }
    } catch (error) {
      // ⚠️ 如果全屏操作失败，给出友善提示
      console.warn('[V2X-Three] 全屏切换失败:', error);
    }
  }

  /*
   * 🔄 关于全屏API的小知识：
   *
   * 全屏API是现代浏览器提供的标准功能：
   * 1. requestFullscreen()：请求进入全屏
   * 2. exitFullscreen()：退出全屏
   * 3. fullscreenElement：当前全屏元素
   * 4. fullscreenchange事件：全屏状态变化
   *
   * 注意事项：
   * - 必须由用户操作触发（点击、按键等）
   * - 不能在页面加载时自动全屏
   * - 某些浏览器可能有安全限制
   *
   * 就像电影院，只有你按下座椅扶手上的按钮才能调节！
   */

  /**
   * 👂 全屏状态变化处理 - 状态同步专家
   *
   * 这个方法就像"状态播报员"📻，当全屏状态发生变化时
   * （无论是通过按钮还是按F11键），都会更新按钮图标和提示文字！
   */
  private onFullscreenChange() {
    // 🔍 检查当前全屏状态
    this.isFullscreen = !!document.fullscreenElement; // !!将值转换为布尔类型

    if (this.fullscreenButton) {
      // 🖼️ 更新按钮图标
      this.fullscreenButton.innerHTML = this.getFullscreenIcon(
        this.isFullscreen
      );
      // 💭 更新鼠标悬停提示
      this.fullscreenButton.title = this.isFullscreen ? '退出全屏' : '全屏';
    }
  }

  /*
   * 🔍 关于!!操作符的小知识：
   *
   * !!是JavaScript中的"双重否定"操作符：
   * - 第一个!：将值转换为布尔值并取反
   * - 第二个!：再次取反，得到原值的布尔表示
   *
   * 例子：
   * !!null → false
   * !!"hello" → true
   * !!0 → false
   * !!document.body → true
   *
   * 这样可以确保isFullscreen始终是true或false，而不是其他类型。
   * 就像问"有没有全屏？"答案只能是"有"或"没有"，不能是"可能"！
   */

  /**
   * 📊 更新性能信息 - 实时数据的更新员
   *
   * 这个方法就像"仪表盘的刷新程序"📈，接收最新的性能数据
   * 并更新显示在界面上，让用户实时了解3D场景的运行状况！
   *
   * @param fps 每秒帧数
   * @param width 画面宽度（像素）
   * @param height 画面高度（像素）
   * @param pixelRatio 像素比例
   */
  updatePerformanceInfo(
    fps: number,
    width: number,
    height: number,
    pixelRatio: number
  ) {
    if (!this.performanceInfo) return; // 🔍 如果性能信息未启用，直接返回

    // 🎯 查找各个数据显示元素
    const fpsElement = this.performanceInfo.querySelector('#v2x-fps');
    const resolutionElement =
      this.performanceInfo.querySelector('#v2x-resolution');
    const pixelRatioElement =
      this.performanceInfo.querySelector('#v2x-pixel-ratio');

    // 📊 更新各项数据
    if (fpsElement) fpsElement.textContent = fps.toString(); // 🎯 更新FPS
    if (resolutionElement) resolutionElement.textContent = `${width}x${height}`; // 📏 更新分辨率
    if (pixelRatioElement)
      pixelRatioElement.textContent = pixelRatio.toFixed(1); // 🔍 更新像素比（保留1位小数）
  }

  /**
   * ⏳ 显示加载指示器 - 开启等待模式
   *
   * 这个方法就像"拉下电影院的幕布"🎭，显示美观的加载动画，
   * 告诉用户"好戏即将开始，请稍等片刻"！
   *
   * @param text 自定义加载文字（可选，默认为"正在加载模型..."）
   */
  showLoading(text: string = '正在加载模型...') {
    if (this.loadingIndicator) {
      // 📝 更新加载文字
      const textElement =
        this.loadingIndicator.querySelector('.v2x-loading-text');
      if (textElement) textElement.textContent = text;

      // 👁️ 显示加载指示器
      this.loadingIndicator.style.display = 'flex';
    }
  }

  /**
   * ✅ 隐藏加载指示器 - 结束等待模式
   *
   * 这个方法就像"拉开电影院的幕布"🎬，隐藏加载动画，
   * 让用户看到精彩的3D世界！
   */
  hideLoading() {
    if (this.loadingIndicator) {
      // 👻 隐藏加载指示器
      this.loadingIndicator.style.display = 'none';
    }
  }

  /**
   * 🗑️ 销毁UI控件 - 彻底清理专家
   *
   * 这个方法就像"剧院闭幕后的清场工作"🧹，清理所有创建的
   * UI元素和事件监听器，防止内存泄漏和资源浪费！
   */
  dispose() {
    // 👂 移除全屏状态变化监听器
    document.removeEventListener(
      'fullscreenchange',
      this.onFullscreenChange.bind(this)
    );

    // 🗑️ 移除加载指示器
    if (this.loadingIndicator) {
      this.loadingIndicator.remove();
    }

    // 🗑️ 移除控件容器（包含所有按钮和信息显示）
    const controlsContainer = this.container.querySelector('.v2x-ui-controls');
    if (controlsContainer) {
      controlsContainer.remove();
    }
  }
}

/*
 * 🎉 总结：UI控制器为你提供了什么？
 *
 * 1. 🎨 现代化界面设计：
 *    - 毛玻璃效果和柔和阴影
 *    - 流畅的悬停和点击动画
 *    - 响应式的主题切换
 *    - 四个角落灵活定位
 *
 * 2. 🔲 完整的全屏体验：
 *    - 一键进入/退出全屏
 *    - 自动同步全屏状态
 *    - 智能图标切换
 *    - 友好的错误处理
 *
 * 3. 📊 专业的性能监控：
 *    - 实时FPS显示
 *    - 分辨率信息
 *    - 像素比显示
 *    - 程序员风格界面
 *
 * 4. ⏳ 优雅的加载体验：
 *    - 美观的云朵动画
 *    - 自定义加载文字
 *    - 全屏覆盖设计
 *    - 渐变背景效果
 *
 * 5. 💾 完善的资源管理：
 *    - 防止重复创建样式
 *    - 正确的事件清理
 *    - 内存泄漏预防
 *    - 优雅的销毁机制
 *
 * 💭 使用场景：
 * 这个UI控制器让你的3D应用拥有专业级的用户界面，
 * 适用于各种需要用户交互的3D场景！
 */
